import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  Competition,
  CompetitionStandings,
  CompetitionTopScorers
} from './competition'
import { CompetitionSerializer } from './competition.serializer'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/core.state'
import { TranslateService } from '@ngx-translate/core'
import { CompetitionMatch } from './competition-match/competition-match'
import { CompetitionTeam } from './competition-team/competition-team'
import { CompetitionMatchEvent } from './competition-match-event/competition-match-event'
import { CompetitionSport } from './competition-sport/competition-sport'

@Injectable()
export class CompetitionService extends ResourceService<Competition> {
  constructor(
    private http: HttpClient,
    private competitionSerializer: CompetitionSerializer,
    private store: Store<AppState>,
    private translate: TranslateService
  ) {
    super(
      http,
      `${environment.backendApiUrl}/competitions`,
      competitionSerializer
    )
  }

  public getTeamStandings(
    teams: CompetitionTeam[],
    matchesOfCompetition: CompetitionMatch[],
    competitionSport: CompetitionSport,
    penaltyMatchEvents: CompetitionMatchEvent[]
  ): CompetitionStandings[] {
    const standings = this.calculateStandings(
      teams,
      matchesOfCompetition,
      competitionSport,
      penaltyMatchEvents
    )

    const sortedStanding = standings.sort((a, b) => {
      // Points
      if (b.points != a.points) {
        return b.points - a.points
      }
      // Sub-points (points amongst teams with an equal amount of points)
      const teamsWithSamePoints = standings.filter((standing) => {
        return standing.points == a.points
      })

      // With two teams equal you take the result of the internal matches.
      // Wins vs Losses (2 wins is on top, 1 win 1 draw is on top)
      // one win one loss or two draws → Goal balance (Doelsaldo)
      // Three or more teams same amount of points → Goal balance (Doelsaldo)
      if (teamsWithSamePoints.length == 2) {
        // take the result of the internal matches
        const teamMatches = matchesOfCompetition.filter(
          (match) =>
            (match.team1Id == a.teamId && match.team2Id == b.teamId) ||
            (match.team2Id == a.teamId && match.team1Id == b.teamId)
        )

        let pointsA = 0
        let pointsB = 0
        let goalDifferenceA = 0
        let goalDifferenceB = 0

        for (let match of teamMatches) {
          if (match.team1Id == a.teamId) {
            if (match.team1Score > match.team2Score) {
              pointsA += competitionSport.pointsWin
            } else if (match.team1Score == match.team2Score) {
              pointsA += competitionSport.pointsDraw
              pointsB += competitionSport.pointsDraw
            } else {
              pointsB += competitionSport.pointsWin
            }

            goalDifferenceA += match.team1Score - match.team2Score
            goalDifferenceB += match.team2Score - match.team1Score
          } else {
            if (match.team1Score > match.team2Score) {
              pointsB += competitionSport.pointsWin
            } else if (match.team1Score == match.team2Score) {
              pointsA += competitionSport.pointsDraw
              pointsB += competitionSport.pointsDraw
            } else {
              pointsA += competitionSport.pointsWin
            }

            goalDifferenceA += match.team2Score - match.team1Score
            goalDifferenceB += match.team1Score - match.team2Score
          }
        }

        if (pointsA != pointsB) {
          return pointsB - pointsA
        }

        if (goalDifferenceA != goalDifferenceB) {
          return goalDifferenceB - goalDifferenceA
        }
      }

      // Goal balance
      if (b.goalDifference != a.goalDifference) {
        return b.goalDifference - a.goalDifference
      }
      // Goals in favor
      if (b.goalsFor != a.goalsFor) {
        return b.goalsFor - a.goalsFor
      }
      // Goals against
      if (b.goalsAgainst != a.goalsAgainst) {
        return a.goalsAgainst - b.goalsAgainst
      }
      // Team name (alphabetic)
      const aName: string = a.teamName
      const bName: string = b.teamName
      return aName.localeCompare(bName)
    })

    const standingWithPosition = sortedStanding.map((standing, index) => {
      return {
        ...standing,
        position: index + 1
      }
    })

    return standingWithPosition
  }

  private calculateStandings(
    teams: CompetitionTeam[],
    matchesOfCompetition: CompetitionMatch[],
    competitionSport: CompetitionSport,
    penaltyMatchEvents: CompetitionMatchEvent[]
  ) {
    let standings = []
    teams.forEach((team) => {
      const matchesOfTeam = matchesOfCompetition.filter(
        (match) =>
          match.team1Score != null &&
          match.team1Score >= 0 &&
          match.team2Score != null &&
          match.team2Score >= 0 &&
          (match.team1Id == team.id || match.team2Id == team.id)
      )
      const gamesPlayed = matchesOfTeam.length

      const wins = matchesOfTeam.filter(
        (match) =>
          (match.team1Id == team.id && match.team1Score > match.team2Score) ||
          (match.team2Id == team.id && match.team2Score > match.team1Score)
      )?.length

      const draws = matchesOfTeam.filter(
        (match) => match.team1Score == match.team2Score
      )?.length

      const losses = matchesOfTeam.filter(
        (match) =>
          (match.team1Id == team.id && match.team1Score < match.team2Score) ||
          (match.team2Id == team.id && match.team2Score < match.team1Score)
      )?.length

      const goalsFor = matchesOfTeam.reduce(
        (total, match) =>
          match.team1Id == team.id
            ? total + match.team1Score
            : total + match.team2Score,
        0
      )
      const goalsAgainst = matchesOfTeam.reduce(
        (total, match) =>
          match.team1Id == team.id
            ? total + match.team2Score
            : total + match.team1Score,
        0
      )

      const pointsWin = competitionSport?.pointsWin
        ? competitionSport?.pointsWin
        : 3
      const pointsDraw = competitionSport?.pointsWin
        ? competitionSport?.pointsDraw
        : 1

      const pointsLoss = competitionSport?.pointsWin
        ? competitionSport?.pointsLoss
        : 0
      let penalties = team.penalty

      penaltyMatchEvents.map((penaltyMatchEvent) => {
        if (penaltyMatchEvent?.competitionTeamId == team.id) {
          penalties += penaltyMatchEvent.amount
        }
      })
      const goalDifference = goalsFor - goalsAgainst
      const points =
        wins * pointsWin + draws * pointsDraw + losses * pointsLoss + penalties

      standings.push({
        teamName: team.description,
        teamId: team.id,
        gamesPlayed,
        wins,
        draws,
        losses,
        goalsFor,
        goalsAgainst,
        goalDifference,
        penalties,
        points
      })
    })

    return standings
  }

  public getTopScorers(
    competionMatchEvents: CompetitionMatchEvent[]
  ): CompetitionTopScorers[] {
    let topScorersPlayerIds = []
    let topScorers = []

    competionMatchEvents.forEach((matchEvent) => {
      if (
        matchEvent?.competitionPlayerId1 &&
        !topScorersPlayerIds.includes(matchEvent?.competitionPlayerId1)
      ) {
        topScorersPlayerIds.push(matchEvent.competitionPlayerId1)
      }
    })

    topScorersPlayerIds.forEach((topScorerPlayerId) => {
      let matchEventsOfPlayer = competionMatchEvents.reduce(
        function (matchEventsOfPlayerObj, matchEventObject) {
          if (
            matchEventObject.hasOwnProperty('competitionPlayerId1') &&
            matchEventObject.competitionPlayerId1 == topScorerPlayerId
          ) {
            if (matchEventsOfPlayerObj.amount > 0) {
              matchEventsOfPlayerObj = {
                ...matchEventObject,
                amount: matchEventsOfPlayerObj.amount + matchEventObject.amount
              }
            } else {
              matchEventsOfPlayerObj = matchEventObject
            }
          }

          return matchEventsOfPlayerObj
        },
        { amount: 0 }
      )

      topScorers.push(matchEventsOfPlayer)
    })

    const sortedTopScorers = topScorers.sort((a, b) => b.amount - a.amount)

    const topScorersWithPosition = sortedTopScorers.map((topScorer, index) => {
      return {
        ...topScorer,
        position: index + 1
      }
    })

    return topScorersWithPosition
  }
}
